import React, { useEffect, useRef, useState } from 'react'
import Icon from '@/components/icon/icon'
import styles from './application-flow-header.module.scss'

export type ApplicationFlowHeaderVisualType = 'transparent' | 'solid'

interface Props {

  /** 左侧返回图标替代节点。 */
  leftIcon?: React.ReactNode

  /** 用户点击返回按钮时交由外层业务容器决定去向。 */
  onBack: () => void

  /** 当前阶段在头部展示的标题。 */
  title: string

  /** 头部视觉模式：solid 表示白底实色，transparent 表示无背景/渐变模式。 */
  visualType?: ApplicationFlowHeaderVisualType
}

/**
 * ApplicationFlowHeader — 申请流程各阶段共用的头部组件
 * @returns {React.ReactElement} 头部展示节点
 */
const ApplicationFlowHeader: React.FC<Props> = React.memo(({ leftIcon, onBack, title, visualType = 'solid' }) => {
  const [scrolled, setScrolled] = useState(false)
  const sentinelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (visualType !== 'transparent') {
      setScrolled(false)
      return undefined
    }

    const handleScroll = (event?: Event) => {
      const target = event?.target as HTMLElement | Document | null
      const targetTop = target && 'scrollTop' in target && typeof target.scrollTop === 'number'
        ? target.scrollTop
        : 0
      const winTop = window.scrollY || window.pageYOffset || 0
      const docTop = document.documentElement?.scrollTop || document.body?.scrollTop || 0
      const scrollTop = Math.max(targetTop, winTop, docTop)

      setScrolled(scrollTop > 0)
    }

    const options = {
      capture: true,
      passive: true,
    }

    handleScroll()
    window.addEventListener('scroll', handleScroll, options)
    document.addEventListener('scroll', handleScroll, options)

    let observer: IntersectionObserver | null = null
    if (sentinelRef.current && typeof IntersectionObserver !== 'undefined') {
      observer = new IntersectionObserver(([entry]) => {
        setScrolled(!entry.isIntersecting)
      })
      observer.observe(sentinelRef.current)
    }

    return () => {
      window.removeEventListener('scroll', handleScroll, options)
      document.removeEventListener('scroll', handleScroll, options)
      if (observer) {
        observer.disconnect()
      }
    }
  }, [visualType])

  return (
    <>
      {visualType === 'transparent' ? <div aria-hidden="true" className={styles.sentinel} ref={sentinelRef} /> : null}
      <header
        className={`${styles.header} ${
          visualType === 'solid' ? styles.solid : styles.transparent
        } ${scrolled ? styles.scrolled : ''}`}
      >
        <button aria-label="返回" className={styles.backButton} onClick={onBack} type="button">
          {leftIcon ?? <Icon type="back" />}
        </button>
        <span className={styles.title}>{title}</span>
      </header>
    </>
  )
})

ApplicationFlowHeader.displayName = 'ApplicationFlowHeader'

export default ApplicationFlowHeader
